import React from "react"
import { motion } from "framer-motion"
import {FileText, Calendar, TrendingUp, Users, ArrowRight, Clock, CheckCircle2, Activity} from 'lucide-react'
import { useGuias } from "../context/GuiasContext"

interface DashboardHomeProps {
  userRole: "admin" | "user"
  userName: string
  onNavigate?: (tab: string) => void
}

export default function DashboardHome({ userRole, userName, onNavigate }: DashboardHomeProps) {
  const { guias, usuarios, logoUrl } = useGuias()

  const hoje = new Date().toISOString().split("T")[0]
  const mesAtual = new Date().getMonth()
  const anoAtual = new Date().getFullYear()

  const guiasHoje = guias.filter(g => g.dataObito === hoje).length
  const guiasMes = guias.filter(g => {
    const data = new Date(g.dataObito)
    return data.getMonth() === mesAtual && data.getFullYear() === anoAtual
  }).length

  const stats = [
    {
      label: "Guias Cadastradas",
      value: guias.length.toString(),
      icon: FileText,
      iconBg: "bg-[#004161]",
      iconColor: "text-white",
      borderColor: "border-l-[#004161]",
      change: "Total de guias no sistema"
    },
    {
      label: "Guias Hoje",
      value: guiasHoje.toString(),
      icon: Calendar,
      iconBg: "bg-emerald-600",
      iconColor: "text-white",
      borderColor: "border-l-emerald-600",
      change: "Emitidas hoje"
    },
    {
      label: "Guias Este Mês",
      value: guiasMes.toString(),
      icon: TrendingUp,
      iconBg: "bg-violet-600",
      iconColor: "text-white",
      borderColor: "border-l-violet-600",
      change: "Emitidas neste mês"
    },
    {
      label: "Usuários Ativos",
      value: usuarios.length.toString(),
      icon: Users,
      iconBg: "bg-orange-500",
      iconColor: "text-white",
      borderColor: "border-l-orange-500",
      change: "Total no sistema",
      adminOnly: true
    },
  ]

  const quickActions = [
    {
      title: "Nova Guia",
      description: "Criar nova guia de sepultamento",
      icon: FileText,
      iconBg: "bg-[#004161]",
      tab: "nova-guia"
    },
    {
      title: "Listar Guias",
      description: "Ver todas as guias cadastradas",
      icon: FileText,
      iconBg: "bg-emerald-600",
      tab: "listar-guias"
    },
    {
      title: "Gerar Pagamento",
      description: "Criar QR Code para pagamento PIX",
      icon: Calendar,
      iconBg: "bg-violet-600",
      tab: "gerar-pagamentos"
    }
  ]

  return (
    <div className="min-h-screen bg-gray-50">

      {/* Header Banner — cor sólida, sem degradê */}
      <motion.div
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-[#004161] rounded-2xl p-6 md:p-8 mb-8 shadow-md"
      >
        <div className="flex flex-col md:flex-row items-center justify-between gap-4 md:gap-6">
          <div className="flex-1 text-center md:text-left">
            <div className="inline-flex items-center gap-2 bg-white/15 px-3 py-1.5 rounded-full mb-3 border border-white/20">
              <CheckCircle2 className="w-4 h-4 text-green-300" />
              <span className="text-sm font-medium text-white">Sistema Online</span>
            </div>
            <h1 className="text-2xl md:text-3xl font-bold text-white mb-2 leading-tight">
              Olá, <span className="text-cyan-200">{userName}</span>
            </h1>
            <p className="text-sm md:text-base text-white/80 font-light">
              Gerencie as guias de sepultamento do município com eficiência e simplicidade
            </p>
          </div>

          <div className="flex-shrink-0 bg-white/10 rounded-xl p-4 border border-white/15">
            <img src={logoUrl} alt="Logo" className="h-14 md:h-16 w-auto" />
          </div>
        </div>
      </motion.div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 mb-8">
        {stats.map((stat, index) => {
          if (stat.adminOnly && userRole !== "admin") return null
          const Icon = stat.icon

          return (
            <motion.div
              key={stat.label}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.08 }}
              whileHover={{ y: -4, transition: { duration: 0.2 } }}
              className={`bg-white rounded-xl p-5 border border-gray-200 border-l-4 ${stat.borderColor} shadow-sm hover:shadow-md transition-all duration-200`}
            >
              <div className="flex items-start justify-between mb-4">
                <div className={`${stat.iconBg} p-3 rounded-lg`}>
                  <Icon className={`w-5 h-5 ${stat.iconColor}`} />
                </div>
              </div>
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">{stat.label}</p>
              <p className="text-4xl font-bold text-gray-900 mb-2">{stat.value}</p>
              <p className="text-xs text-gray-400 flex items-center gap-1.5">
                <Clock className="w-3.5 h-3.5" />
                {stat.change}
              </p>
            </motion.div>
          )
        })}
      </div>

      {/* Quick Actions */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.35 }}
        className="mb-8"
      >
        <div className="flex items-center gap-3 mb-5">
          <div className="w-1 h-6 bg-[#004161] rounded-full"></div>
          <h2 className="text-lg font-bold text-gray-800">Ações Rápidas</h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {quickActions.map((action, index) => {
            const Icon = action.icon
            return (
              <motion.button
                key={action.title}
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4 + index * 0.08 }}
                whileHover={{ y: -4, transition: { duration: 0.15 } }}
                whileTap={{ scale: 0.98 }}
                onClick={() => onNavigate?.(action.tab)}
                className="group bg-white rounded-xl p-5 text-left border border-gray-200 hover:border-[#004161] shadow-sm hover:shadow-md transition-all duration-200"
              >
                <div className="flex items-start justify-between mb-4">
                  <div className={`${action.iconBg} p-3 rounded-lg`}>
                    <Icon className="w-5 h-5 text-white" />
                  </div>
                  <ArrowRight className="w-4 h-4 text-gray-300 group-hover:text-[#004161] group-hover:translate-x-1 transition-all duration-200" />
                </div>
                <h3 className="text-base font-bold text-gray-800 mb-1 group-hover:text-[#004161] transition-colors">{action.title}</h3>
                <p className="text-sm text-gray-500">{action.description}</p>
              </motion.button>
            )
          })}
        </div>
      </motion.div>

      {/* Recent Activity */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.5 }}
        className="bg-white rounded-xl p-6 shadow-sm border border-gray-200 mb-8"
      >
        <div className="flex items-center gap-3 mb-6">
          <div className="w-1 h-6 bg-[#004161] rounded-full"></div>
          <h3 className="text-lg font-bold text-gray-800">Atividades Recentes</h3>
          <span className="ml-auto bg-gray-100 text-gray-500 text-xs font-medium px-2.5 py-1 rounded-full">
            Últimas {Math.min(guias.length, 5)}
          </span>
        </div>

        <div className="divide-y divide-gray-100">
          {guias.slice(-5).reverse().map((guia, index) => (
            <motion.div
              key={guia._id}
              initial={{ opacity: 0, x: -16 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.6 + index * 0.05 }}
              className="group flex items-center gap-4 py-3.5 hover:bg-gray-50 rounded-lg px-2 transition-colors duration-150 cursor-pointer"
            >
              <div className="w-10 h-10 bg-[#004161] rounded-lg flex items-center justify-center flex-shrink-0">
                <FileText className="w-5 h-5 text-white" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-gray-800 truncate">
                  Guia {guia.numero} — {guia.nomeFalecido}
                </p>
                <p className="text-xs text-gray-400 flex items-center gap-1.5 mt-0.5">
                  <Activity className="w-3 h-3" />
                  {new Date(guia.createdAt).toLocaleDateString("pt-BR")} às{" "}
                  {new Date(guia.createdAt).toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" })}
                </p>
              </div>
              <ArrowRight className="w-4 h-4 text-gray-300 group-hover:text-[#004161] transition-colors flex-shrink-0" />
            </motion.div>
          ))}

          {guias.length === 0 && (
            <div className="text-center py-12">
              <div className="w-14 h-14 bg-gray-100 rounded-xl flex items-center justify-center mx-auto mb-4">
                <FileText className="w-7 h-7 text-gray-400" />
              </div>
              <p className="text-sm font-medium text-gray-500">Nenhuma atividade recente</p>
              <p className="text-xs text-gray-400 mt-1">As atividades aparecerão aqui quando forem criadas</p>
            </div>
          )}
        </div>
      </motion.div>

      {/* Footer Info */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.7 }}
        className="bg-white rounded-xl p-6 border border-gray-200 shadow-sm text-center"
      >
        <div className="flex flex-col items-center gap-1.5">
          <h4 className="font-bold text-gray-800 text-base">Prefeitura Municipal de São José do Seridó</h4>
          <p className="text-sm text-gray-500">Rua Vicente Pereira, 87 — Centro</p>
          <p className="text-sm text-gray-500">CNPJ: 08.096.083/0001-76 | Contato: (84) 3478-2178</p>
          <div className="mt-4 pt-4 border-t border-gray-100 w-full">
            <p className="text-xs text-gray-400">
              Desenvolvido por <span className="font-semibold text-[#004161]">Railson Abreu</span>
            </p>
          </div>
        </div>
      </motion.div>

    </div>
  )
}
